import React, { useState, useRef, useEffect, useContext, useMemo, useCallback } from 'react';
import { Box, IconButton, Typography, Paper, Divider, Chip, Badge } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateCalendar, PickersDay } from '@mui/x-date-pickers';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { format, isToday, isSameDay, isSameMonth } from 'date-fns';
import { 
  toLocalDateString, 
  createLocalDate, 
  formatLocalDate, 
  today, 
  createDate,
  createDateForDayOfMonth
} from '../utils/dateUtils';
import { ThemeContext } from '../App';
import { accountsApi, recurringTransactionsApi } from '../services/api';
import { debug } from '../utils/debug';
import { ensureComponentInViewport } from '../utils/detachableHelpers';

// Static (renders nothing, no props/closures) - hoisted so it keeps a
// stable identity across renders, same reasoning as ServerDay below: it's
// passed to DateCalendar as a component type via slots.toolbar, and a fresh
// arrow function there every render is a fresh component type to React.
const hideCalendarToolbar = () => null;

const DetachableCalendar = ({
  onClose,
  onDateChange 
}) => {
  const {
    mode,
    weatherCondition,
    weatherThemeEnabled,
    weatherThemeMode,
    selectedWeatherTheme,
    solidDetachableComponents
  } = useContext(ThemeContext);

  const isDarkMode = mode === 'dark';
  const theme = useTheme();

  // Get the effective weather condition based on theme mode
  const effectiveWeatherCondition = weatherThemeEnabled
    ? (weatherThemeMode === 'manual' ? selectedWeatherTheme : weatherCondition)
    : null;
  
  debug('calendar', 'Weather condition', effectiveWeatherCondition);
  debug('calendar', 'Weather theme enabled', weatherThemeEnabled);

  // Moved up from further down in the component (it used to sit right next
  // to getAccentColor()) so it's available to hasDueItemsOnDate/ServerDay's
  // useCallback dependency arrays below, without which those callbacks would
  // reference an as-yet-uninitialized const and throw at render time.
  const accentColor = (weatherThemeEnabled && effectiveWeatherCondition)
    ? getAccentColor(effectiveWeatherCondition, isDarkMode)
    : (isDarkMode ? '#bb86fc' : '#1976d2');

  // Position on the right side by default, with localStorage support
  const [position, setPosition] = useState(() => {
    // Get position and ensure it's within viewport
    return ensureComponentInViewport(
      'calendarPosition',
      { x: window.innerWidth - 380, y: 80 },
      320,
      400
    );
  });
  
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const calendarRef = useRef(null);
  
  // Initialize calendar date with current month
  const [calendarDate, setCalendarDate] = useState(today());
  const [selectedDate, setSelectedDate] = useState(today());

  // Add state for credit cards and recurring bills
  const [creditCards, setCreditCards] = useState([]);
  const [recurringBills, setRecurringBills] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch credit cards and recurring bills data
  useEffect(() => {
    const fetchFinancialData = async () => {
      setIsLoading(true);
      try {
        // Fetch credit cards (accounts with type 'credit')
        const accountsResponse = await accountsApi.getAll();
        const creditCardsData = accountsResponse.data.filter(
          account => account.account_type === 'credit' && account.due_date
        );
        setCreditCards(creditCardsData);
        
        // Fetch recurring transactions (bills)
        const recurringResponse = await recurringTransactionsApi.getAll();
        const recurringBillsData = recurringResponse.data.filter(
          bill => bill.is_active
        );
        setRecurringBills(recurringBillsData);
      } catch (error) {
        debug('calendar', 'Error fetching financial data', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchFinancialData();
  }, []);

  // Add function to handle monthly recurring bills for future months.
  // useCallback'd (deps: creditCards/recurringBills only) so it - and, more
  // importantly, ServerDay below, which closes over it - keeps a stable
  // identity across renders that don't actually change the due-item data.
  // ServerDay is passed to DateCalendar as slots.day, i.e. as a component
  // *type*; a fresh function identity every render (as before) meant React
  // treated every calendar day cell as a brand-new component type on every
  // render and remounted the entire day grid instead of reconciling it -
  // including on every animation-frame tick while this widget is being
  // dragged, since dragging's position state has nothing to do with due
  // dates.
  const hasDueItemsOnDate = useCallback((day) => {
    // Skip check for null dates
    if (!day) return false;
    
    // For credit cards, check if we have any due on this day of the month
    const hasCreditCardDue = creditCards.some(card => {
      if (!card.due_date) return false;
      
      // Parse the date string properly to avoid timezone issues
      const dateParts = card.due_date.split('-');
      if (dateParts.length !== 3) return false;
      
      const year = parseInt(dateParts[0]);
      const month = parseInt(dateParts[1]) - 1; // JavaScript months are 0-indexed
      const dayOfMonth = parseInt(dateParts[2]);
      
      // Create date object in local timezone to avoid timezone shift
      const dueDate = new Date(year, month, dayOfMonth);
      dueDate.setHours(0, 0, 0, 0); // Set to start of day for consistent comparison
      
      // For credit cards, match day of month (assume monthly cycle)
      return dueDate.getDate() === day.getDate();
    });
    
    // For bills, check if any are due on this exact date or recurring on this day of month
    const hasBillDue = recurringBills.some(bill => {
      if (!bill.next_due_date) return false;
      
      // Parse date properly to avoid timezone issues
      // Split the date string and create a date in local timezone
      const dateParts = bill.next_due_date.split('-');
      if (dateParts.length !== 3) return false;
      
      const year = parseInt(dateParts[0]);
      const month = parseInt(dateParts[1]) - 1; // JavaScript months are 0-indexed
      const dayOfMonth = parseInt(dateParts[2]);
      
      // Create date object in local timezone to avoid timezone shift
      const nextDueDate = createDate(year, month, dayOfMonth);
      nextDueDate.setHours(0, 0, 0, 0); // Set to start of day for consistent comparison
      
      // For monthly bills, match the day of month
      if (bill.frequency === 'MONTHLY') {
        return nextDueDate.getDate() === day.getDate();
      }
      
      // For non-monthly bills, only match exact date
      return isSameDay(nextDueDate, day);
    });
    
    return hasCreditCardDue || hasBillDue;
  }, [creditCards, recurringBills]);

  // Custom day rendering to show indicators for days with events.
  // useCallback'd for the same reason as hasDueItemsOnDate above - this is
  // passed to DateCalendar as slots.day, i.e. as a component type, so it
  // needs a stable identity to avoid remounting every day cell on every
  // unrelated render.
  const ServerDay = useCallback((props) => {
    const { day, outsideCurrentMonth, ...other } = props;

    // Use the new function to check for events on any date
    const hasEvents = hasDueItemsOnDate(day);

    return (
      <Badge
        key={day.toString()}
        overlap="circular"
        badgeContent={hasEvents ? '•' : undefined}
        sx={{
          '& .MuiBadge-badge': {
            bottom: 5,
            fontSize: '12px',
            color: accentColor,
            opacity: outsideCurrentMonth ? 0.5 : 1,
          }
        }}
      >
        <PickersDay 
          {...other} 
          outsideCurrentMonth={outsideCurrentMonth} 
          day={day} 
        />
      </Badge>
    );
  }, [hasDueItemsOnDate, accentColor]);

  // Update getDateDueItems to handle selected date displays including future months
  const getDateDueItems = (date) => {
    if (!date) return { dueCreditCards: [], dueBills: [] };
    
    // Get credit cards due on the selected date (match day of month)
    const dueCreditCards = creditCards.filter(card => {
      if (!card.due_date) return false;
      
      // Parse the date string properly to avoid timezone issues
      const dateParts = card.due_date.split('-');
      if (dateParts.length !== 3) return false;
      
      const year = parseInt(dateParts[0]);
      const month = parseInt(dateParts[1]) - 1; // JavaScript months are 0-indexed
      const dayOfMonth = parseInt(dateParts[2]);
      
      // Create date object in local timezone to avoid timezone shift
      const dueDate = createDate(year, month, dayOfMonth);
      dueDate.setHours(0, 0, 0, 0); // Set to start of day for consistent comparison
      
      // For credit cards, just match the day of month (for future months)
      const dayMatch = dueDate.getDate() === date.getDate();
      
      // If we're viewing the exact due date month, require full date match
      if (dueDate.getMonth() === date.getMonth() && 
          dueDate.getFullYear() === date.getFullYear()) {
        return dayMatch;
      }
      
      // For other months, just show on the same day number
      return dayMatch;
    });
    
    // Get recurring bills due on the selected date
    const dueBills = recurringBills.filter(bill => {
      if (!bill.next_due_date) return false;
      
      // Parse date properly to avoid timezone issues
      // Split the date string and create a date in local timezone
      const dateParts = bill.next_due_date.split('-');
      if (dateParts.length !== 3) return false;
      
      const year = parseInt(dateParts[0]);
      const month = parseInt(dateParts[1]) - 1; // JavaScript months are 0-indexed
      const dayOfMonth = parseInt(dateParts[2]);
      
      // Create date object in local timezone to avoid timezone shift
      const nextDueDate = createDate(year, month, dayOfMonth);
      nextDueDate.setHours(0, 0, 0, 0); // Set to start of day for consistent comparison
      
      // For monthly recurring bills, match day of month for future display
      if (bill.frequency === 'MONTHLY') {
        return nextDueDate.getDate() === date.getDate();
      }
      
      // For non-monthly bills, only match if it's the exact date
      return isSameDay(nextDueDate, date);
    });
    
    return { dueCreditCards, dueBills };
  };
  
  // Get today's due items (we'll still use this for the calendar markers)
  const getTodaysDueItems = () => {
    return getDateDueItems(today());
  };

  // Get upcoming due items (next 7 days)
  const getUpcomingDueItems = () => {
    const todayDate = today();
    // Set today to start of day to avoid timezone issues
    todayDate.setHours(0, 0, 0, 0);
    
    const nextWeek = new Date(todayDate);
    nextWeek.setDate(todayDate.getDate() + 7);
    
    // Get credit cards due in the next 7 days (excluding today)
    const upcomingCreditCards = creditCards.filter(card => {
      if (!card.due_date) return false;
      
      // Parse the date string properly to avoid timezone issues
      const dateParts = card.due_date.split('-');
      if (dateParts.length !== 3) return false;
      
      const year = parseInt(dateParts[0]);
      const month = parseInt(dateParts[1]) - 1; // JavaScript months are 0-indexed
      const dayOfMonth = parseInt(dateParts[2]);
      
      // Create date object in local timezone to avoid timezone shift
      const dueDate = createDate(year, month, dayOfMonth);
      dueDate.setHours(0, 0, 0, 0); // Set to start of day for consistent comparison
      
      return dueDate > todayDate && dueDate <= nextWeek && !isSameDay(dueDate, todayDate);
    });
    
    // Get recurring bills due in the next 7 days (excluding today)
    const upcomingBills = recurringBills.filter(bill => {
      if (!bill.next_due_date) return false;
      
      // Parse date properly to avoid timezone issues
      // Split the date string and create a date in local timezone
      const dateParts = bill.next_due_date.split('-');
      if (dateParts.length !== 3) return false;
      
      const year = parseInt(dateParts[0]);
      const month = parseInt(dateParts[1]) - 1; // JavaScript months are 0-indexed
      const dayOfMonth = parseInt(dateParts[2]);
      
      // Create date object in local timezone to avoid timezone shift
      const nextDueDate = createDate(year, month, dayOfMonth);
      nextDueDate.setHours(0, 0, 0, 0); // Set to start of day for consistent comparison
      
      return nextDueDate > todayDate && nextDueDate <= nextWeek && !isSameDay(nextDueDate, todayDate);
    });
    
    return { upcomingCreditCards, upcomingBills };
  };

  // getDateDueItems(selectedDate) and getUpcomingDueItems() are each called
  // 4 separate times per render at their respective call sites below, every
  // call redoing the same filter passes (string-split/parseInt/Date
  // construction per credit card and bill) for identical inputs. Cache each
  // result once per render instead.
  const selectedDateDueItems = useMemo(
    () => getDateDueItems(selectedDate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedDate, creditCards, recurringBills]
  );
  const upcomingDueItems = useMemo(
    () => getUpcomingDueItems(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [creditCards, recurringBills]
  );

  // Handle mouse down event to start dragging
  const handleMouseDown = (e) => {
    if (e.target.closest('.close-btn') || e.target.closest('button') || 
        e.target.closest('.MuiPickersDay-root')) return; // Don't start drag when clicking interactive elements
    
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  // Handle mouse move event when dragging.
  // Coalesced into at most one setPosition (and therefore one re-render)
  // per animation frame via requestAnimationFrame, instead of one per
  // mousemove event - mousemove can fire at a much higher rate than the
  // display can even show, so this was re-rendering the whole widget far
  // more often than any of it was ever visible for. Same fix as
  // DetachableClock.js/DetachableCalculator.js.
  const dragPositionRef = useRef(null);
  const dragRafRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    dragPositionRef.current = {
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    };

    if (dragRafRef.current === null) {
      dragRafRef.current = requestAnimationFrame(() => {
        dragRafRef.current = null;
        setPosition(dragPositionRef.current);
      });
    }
  };

  // Handle mouse up event to stop dragging
  const handleMouseUp = (e) => {
    // Cancel any still-pending throttled move update - it holds a stale
    // position that would otherwise land after (and overwrite) the final
    // adjusted one set below.
    if (dragRafRef.current !== null) {
      cancelAnimationFrame(dragRafRef.current);
      dragRafRef.current = null;
    }

    if (isDragging) {
      // Calculate raw position first
      const rawPosition = {
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      };

      // Ensure the position is within the viewport bounds
      const adjustedPosition = ensureComponentInViewport(
        'calendarPosition',
        rawPosition,
        320,
        400
      );

      // Update state with the adjusted position
      setPosition(adjustedPosition);
    }
    setIsDragging(false);
  };

  // Add/remove event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      if (dragRafRef.current !== null) {
        cancelAnimationFrame(dragRafRef.current);
        dragRafRef.current = null;
      }
    };
  }, [isDragging, dragStart, position]);

  // Add window resize handler to keep component in viewport
  useEffect(() => {
    const handleResize = () => {
      // Only update position if not currently dragging
      if (!isDragging) {
        const newPosition = ensureComponentInViewport(
          'calendarPosition',
          { x: window.innerWidth - 380, y: 80 },
          320,
          400,
          true // Maintain right distance
        );
        setPosition(newPosition);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isDragging]);

  // Save position to localStorage when component unmounts
  useEffect(() => {
    return () => {
      localStorage.setItem('calendarPosition', JSON.stringify(position));
    };
  }, [position]);

  // Navigation functions for calendar
  const goToPreviousMonth = () => {
    setCalendarDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };

  const goToNextMonth = () => {
    setCalendarDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  const goToToday = () => {
    const todayDate = today();
    setCalendarDate(todayDate);
    setSelectedDate(todayDate);
    if (onDateChange) {
      onDateChange(todayDate);
    }
  };

  const handleDateSelect = (newDate) => {
    setSelectedDate(newDate);
    if (onDateChange) {
      onDateChange(newDate);
    }
  };

  // Format weekdays to use 3-letter abbreviations
  const formatWeekday = (day) => {
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return weekdays[day];
  };

  const pageBackgroundColor = theme.palette?.background?.default || (isDarkMode ? '#121212' : '#f5f5f5');

  // Dynamic colors sourced from the active theme palette
  const bgColor = pageBackgroundColor;

  const textColor = theme.palette?.text?.primary || (isDarkMode ? '#e0e0e0' : '#212529');

  const secondaryTextColor = theme.palette?.text?.secondary || (isDarkMode ? '#a0a0a0' : '#6c757d');

  const borderColor = theme.palette?.divider || (isDarkMode ? '#333333' : '#dee2e6');

  // Get accent color based on weather condition
  function getAccentColor(condition, isDark) {
    condition = condition.toLowerCase();
    
    switch(condition) {
      case 'clear':
        return isDark ? '#ffa000' : '#ff9800'; // Amber for clear/sunny
      case 'clouds':
        return isDark ? '#78909c' : '#607d8b'; // Blue-gray for clouds
      case 'rain':
      case 'drizzle':
        return isDark ? '#26c6da' : '#00acc1'; // Cyan/teal for rain
      case 'snow':
        return isDark ? '#90caf9' : '#42a5f5'; // Blue for snow
      case 'thunderstorm':
        return isDark ? '#9575cd' : '#5e35b1'; // Purple for thunderstorm
      case 'spring':
        return isDark ? '#66bb6a' : '#81c784'; // Green for spring
      case 'summer':
        return isDark ? '#26c6da' : '#00bcd4'; // Cyan for summer
      case 'autumn':
        return isDark ? '#ff7043' : '#ff5722'; // Deep orange for autumn
      case 'winter':
        return isDark ? '#90caf9' : '#2196f3'; // Blue for winter
      default:
        return isDark ? '#bb86fc' : '#1976d2';
    }
  }

  // Weather icon to display as a decorative element
  const getWeatherIcon = () => {
    if (!effectiveWeatherCondition) return '';
    
    switch(effectiveWeatherCondition.toLowerCase()) {
      case 'clear': return '☀️';
      case 'clouds': return '☁️';
      case 'rain': case 'drizzle': return '🌧️';
      case 'snow': return '❄️';
      case 'thunderstorm': return '⚡';
      case 'spring': return '🌱';
      case 'summer': return '🏖️';
      case 'autumn': return '🍂';
      case 'winter': return '☃️';
      default: return '';
    }
  };

  return (
    <Paper
      ref={calendarRef}
      elevation={6}
      sx={{
        position: 'fixed',
        top: `${position.y}px`,
        left: `${position.x}px`,
        zIndex: 1050,
        background: bgColor,
        color: textColor,
        borderRadius: '12px',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        width: '280px',
        transition: 'background 0.5s ease, color 0.3s ease, box-shadow 0.3s ease, transform 0.2s ease',
        '&:hover': {
          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)',
          transform: 'translateY(-2px)'
        },
        overflow: 'hidden',
        border: `1px solid ${borderColor}`
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Header with month/year and close button */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        background: 'transparent',
        borderBottom: '1px solid',
        borderColor: borderColor,
        py: 1,
        px: 2
      }}>
        <Typography variant="subtitle1" sx={{ 
          fontWeight: 'medium', 
          color: textColor,
          display: 'flex',
          alignItems: 'center' 
        }}>
          {weatherThemeEnabled && getWeatherIcon()} 
          <span style={{ marginLeft: weatherThemeEnabled ? '4px' : 0 }}>
            {format(calendarDate, 'MMMM yyyy')}
          </span>
        </Typography>
        <IconButton 
          size="small" 
          className="close-btn"
          onClick={onClose}
          sx={{ 
            color: secondaryTextColor,
            '&:hover': {
              color: textColor
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Navigation row */}
      <Box sx={{ 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 0.5
      }}>
        <IconButton 
          size="small" 
          onClick={goToPreviousMonth}
          sx={{ color: secondaryTextColor }}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>

        <Box 
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            color: secondaryTextColor,
            '&:hover': { color: accentColor },
            px: 1,
            py: 0.5,
            borderRadius: 1,
            transition: 'color 0.2s ease'
          }}
          onClick={goToToday}
        >
          <CalendarTodayIcon fontSize="small" sx={{ mr: 0.5 }} />
          <Typography variant="body2">Today</Typography>
        </Box>

        <IconButton 
          size="small" 
          onClick={goToNextMonth}
          sx={{ color: secondaryTextColor }}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Calendar */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DateCalendar
          value={calendarDate}
          onChange={handleDateSelect}
          disableFuture={false}
          showDaysOutsideCurrentMonth
          dayOfWeekFormatter={formatWeekday}
          slots={{
            toolbar: hideCalendarToolbar, // Hide default toolbar
            day: ServerDay
          }}
          sx={{
            width: '100%',
            // Add very aggressive padding reduction to remove gap
            pt: 0, pb: 0, px: 0,
            height: 'auto',
            minHeight: 'auto',
            '& .MuiDayCalendar-root': {
              maxHeight: '220px',
              minHeight: 'auto',
              pt: 0, pb: 0
            },
            '& .MuiPickersDay-root': {
              fontWeight: 'medium',
              borderRadius: '4px',
              color: textColor,
              transition: 'background-color 0.3s ease, color 0.3s ease'
            },
            '& .MuiDayCalendar-weekDayLabel': {
              color: secondaryTextColor,
              transition: 'color 0.3s ease'
            },
            '& .MuiPickersDay-today': {
              border: `1px solid ${accentColor} !important`,
              color: accentColor,
              backgroundColor: 'transparent'
            },
            '& .MuiPickersDay-root.Mui-selected': {
              backgroundColor: `${accentColor} !important`,
              color: isDarkMode ? '#fff' : '#fff'
            },
            '& .MuiPickersDay-dayOutsideMonth': {
              color: `${secondaryTextColor} !important`, 
              opacity: 0.5
            },
            // Make days of week and grid smaller
            '& .MuiDayCalendar-header, .MuiDayCalendar-weekContainer': {
              mx: 1
            },
            '& .MuiTypography-caption': {
              height: '24px',
              lineHeight: '24px'
            },
            '& .MuiPickersDay-root': {
              height: '32px',
              width: '32px',
              fontSize: '0.85rem'
            },
            // Force remove any toolbar
            '& .MuiPickersCalendarHeader-root': {
              display: 'none'
            },
            // Target the space at the bottom of the calendar specifically
            '& .MuiPickersSlideTransition-root': {
              marginBottom: '-35px' // Reduced from -45px to add more padding
            },
            // Add direct negative margin to the bottom
            marginBottom: '-55px', // Reduced from -65px
            // Adjust bottom padding on month container
            '& .MuiDayCalendar-monthContainer': {
              pb: 0,
              mb: 0
            },
            // Keep the negative margin but adjust to a more precise value
            mb: -3.5
          }}
        />
      </LocalizationProvider>

      {/* Footer with selected date */}
      <Box sx={{ 
        textAlign: 'center',
        py: 1,
        borderTop: '1px solid',
        borderColor: borderColor,
        position: 'relative',
        mt: '-22px', // Reduced from -30px to add more padding
        zIndex: 1
      }}>
        <Typography variant="caption" sx={{ color: secondaryTextColor }}>
          {format(selectedDate, 'EEEE, MMMM d, yyyy')}
        </Typography>
      </Box>
      
      {/* Financial Information Section */}
      <Box sx={{ 
        px: 2, 
        py: 1, // Reduced from 1.5 since we removed the title
        borderTop: '1px solid',
        borderColor: borderColor,
        backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.03)',
        mt: '-2px', // Remove any gap between footer and financial section
        position: 'relative',
        zIndex: 0
      }}>
        {isLoading ? (
          <Typography variant="caption" sx={{ color: secondaryTextColor, fontStyle: 'italic' }}>
            Loading financial data...
          </Typography>
        ) : (
          <>
            {/* Credit Cards Due on Selected Date */}
            {selectedDateDueItems.dueCreditCards.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  color: textColor,
                  fontWeight: 'medium',
                  mb: 0.5
                }}>
                  <CreditCardIcon fontSize="small" sx={{ mr: 0.5, color: secondaryTextColor }} />
                  Credit Card Payments Due:
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selectedDateDueItems.dueCreditCards.map(card => (
                    <Chip 
                      key={card.id} 
                      label={card.account} 
                      size="small"
                      sx={{ 
                        backgroundColor: isDarkMode ? 'rgba(255, 82, 82, 0.15)' : 'rgba(255, 82, 82, 0.1)',
                        color: isDarkMode ? '#ff8a80' : '#d32f2f',
                        fontSize: '0.7rem'
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
            
            {/* Recurring Transactions Due on Selected Date */}
            {selectedDateDueItems.dueBills.length > 0 && (
              <Box>
                <Typography variant="caption" sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  color: textColor,
                  fontWeight: 'medium',
                  mb: 0.5
                }}>
                  <ReceiptIcon fontSize="small" sx={{ mr: 0.5, color: secondaryTextColor }} />
                  Recurring Transactions Due:
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selectedDateDueItems.dueBills.map(bill => (
                    <Chip 
                      key={bill.id} 
                      label={`${bill.name} (${bill.currency} ${bill.amount.toFixed(2)})`}
                      size="small"
                      sx={{ 
                        backgroundColor: bill.type === 'expense' 
                          ? (isDarkMode ? 'rgba(41, 121, 255, 0.15)' : 'rgba(41, 121, 255, 0.1)') 
                          : (isDarkMode ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.1)'),
                        color: bill.type === 'expense'
                          ? (isDarkMode ? '#82b1ff' : '#1565c0')
                          : (isDarkMode ? '#a5d6a7' : '#2e7d32'),
                        fontSize: '0.7rem'
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
            
            {/* Show upcoming items only when looking at today */}
            {isToday(selectedDate) && (upcomingDueItems.upcomingCreditCards.length > 0 || 
              upcomingDueItems.upcomingBills.length > 0) && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" sx={{ 
                  color: secondaryTextColor,
                  fontWeight: 'medium',
                  display: 'block',
                  mb: 0.5
                }}>
                  Upcoming (Next 7 Days):
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {upcomingDueItems.upcomingCreditCards.map(card => {
                    // Parse date properly to avoid timezone issues
                    const dateParts = card.due_date.split('-');
                    const year = parseInt(dateParts[0]);
                    const month = parseInt(dateParts[1]) - 1; // JavaScript months are 0-indexed
                    const dayOfMonth = parseInt(dateParts[2]);
                    const displayDate = createDate(year, month, dayOfMonth);
                    
                    return (
                      <Chip 
                        key={card.id}
                        label={`${card.account} (${format(displayDate, 'MMM d')})`}
                        size="small"
                        sx={{ 
                          backgroundColor: 'transparent',
                          border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`
                        }}
                      />
                    );
                  })}
                  {upcomingDueItems.upcomingBills.map(bill => {
                    // Parse date properly to avoid timezone issues
                    const dateParts = bill.next_due_date.split('-');
                    const year = parseInt(dateParts[0]);
                    const month = parseInt(dateParts[1]) - 1; // JavaScript months are 0-indexed
                    const dayOfMonth = parseInt(dateParts[2]);
                    const displayDate = createDate(year, month, dayOfMonth);
                    
                    return (
                      <Chip 
                        key={bill.id}
                        label={`${bill.name} (${format(displayDate, 'MMM d')})`}
                        size="small"
                        sx={{ 
                          backgroundColor: 'transparent',
                          border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
};

export default DetachableCalendar;